import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import { useDispatch, useSelector } from 'react-redux';
import { round } from 'lodash';
import {
  Category,
  CURRENCY,
  Event,
  PRE_SERVICE_LICENCE_TYPE,
  REGISTRATION_STATUS,
  SERVICE_GROUP_TYPE,
} from '@root/src/types';
import { routeRegex } from '@root/src/services/routeList';
import { dashIfNullOrEmpty } from '@utils/gobal';
import { mediaBreakpoint } from '@services/responsiveProvider';
import { textH4, textP } from '@features/ui/styles/textStyles';
import { selectEventCategory, selectEventDetail } from '@features/eventDetail/slices/eventDetailServerSlice';
import ToggleCard, { TOGGLE_CARD_SIZES } from '@features/ui/components/ToggleCard';
import { table } from '@features/eventDetail/components/EventCategoryList/styles';
import EventCategoryListContent from '@features/eventDetail/components/EventCategoryListContent';
import { useGetEventData } from '@features/eventDetail/GetEventData';
import Loader from '@features/ui/components/Loader';
import { selectIsAuthenticated } from '@features/users/slices/userSlice';
import { selectSubscriptionFor, toggleSubscription, selectSubscriptionIsSelected, selectSubscriptionPack, selectEventsWithSelectedCategory } from '@features/subscriptions/slices/formSubscriptionSlice';
import { selectSerieRegistrationsOpen } from '@features/serieDetail/slices/serieDetailServerSlice';

interface CategoryLineProps {
  eventId: number;
  categoryId: number;
}

const computeTags = (category: Category, event: Event, t) => {
  const eventClosed = event.quotas?.full
    || [REGISTRATION_STATUS.CLOSED, REGISTRATION_STATUS.CANCELLED, REGISTRATION_STATUS.NOT_YET_OPEN]
      .includes(event.status.registration);

  const tags = [];

  if (category.quotas?.full && !eventClosed) {
    tags.push(t('categoryLine_completed.message'));
  }

  if (
    category.preServices.license
    && category.preServices.licenseMandatory
    && category.preServices.licenseType === PRE_SERVICE_LICENCE_TYPE.CAS
  ) {
    tags.push(t('categoryLine_casCategory.message'));
  }

  if (category.preServices.fileUploadMandatory) {
    tags.push(t('categoryLine_certificateRequired.message'));
  }

  return tags;
};

const CategoryLine = ({ eventId, categoryId }: CategoryLineProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const getEventData = useGetEventData();

  const event = useSelector(selectEventDetail(eventId));
  const registrationsOpen = useSelector(selectSerieRegistrationsOpen);
  const category = useSelector(selectEventCategory(eventId, categoryId));
  const logged = useSelector(selectIsAuthenticated);
  const subscriptionFor = useSelector(selectSubscriptionFor);
  const pack = useSelector(selectSubscriptionPack);
  const categoryIsSelected = useSelector(selectSubscriptionIsSelected(category._idMso));
  const eventsWithSelectedCategory = useSelector(selectEventsWithSelectedCategory);

  const { pathname } = useRouter();
  const isSeriePage = routeRegex.serie.test(pathname);

  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    const loadData = async () => {
      if (isOpen && logged) {
        setLoading(true);
        await getEventData.getEventCategoryService(event._idMso, categoryId, SERVICE_GROUP_TYPE.PRE_SERVICES);
      }
      if (logged) {
        await getEventData.getEventCategoryQuota(event._idMso, categoryId);
      }
      setLoading(false);
    };
    loadData();
  }, [isOpen, logged, subscriptionFor]);

  const tags = computeTags(category, event, t);

  const chip = category.team?.nbMembers != null && t('categoryLine_inTeams.message', { count: category.team?.nbMembers });

  const lineDisabled = !logged
    || (isSeriePage && !registrationsOpen)
    || (eventsWithSelectedCategory[eventId] && eventsWithSelectedCategory[eventId] !== categoryId);

  const handleCheckboxClick = () => {
    dispatch(toggleSubscription({
      category,
      event,
    }));
  };

  return (
    <StyledToggleCard
      alignArrowOnTopOnMobile
      size={TOGGLE_CARD_SIZES.MEDIUM}
      header={<CategoryLineTableRow category={category} currency={event.currency} />}
      body={loading ? <Loader /> : <EventCategoryListContent eventId={eventId} categoryId={categoryId} />}
      chip={chip}
      tags={tags.length > 0 ? tags : undefined}
      onSetOpen={(open) => setIsOpen(open)}
      disabled={lineDisabled}
      onSelectClick={pack && handleCheckboxClick}
      isSelected={categoryIsSelected}
      dataQa={`eventDetail-category-item-${category._idMso}`}
    />
  );
};

export default CategoryLine;

const StyledToggleCard = styled(ToggleCard)`
  margin-top: 2px;
  margin-bottom: 2px;

  ${mediaBreakpoint.mobile} {
    :first-child {
      margin-top: 20px;
    }
  }
`;

const CategoryLineTableRow = ({ category, currency }: { category: Category, currency: CURRENCY }) => {
  const { t } = useTranslation();
  return (
    <RowContainer>
      <div className="CategoryLineTableRow_title">{category.name}</div>
      <div className="CategoryLineTableRow_gender">{t(`eventCategory_gender${category.gender}.message`)}</div>
      <div className="CategoryLineTableRow_ageRange">
        {t('eventCategory_ageRange.message', {
          min: category.ageMin,
          max: category.ageMax,
        })}

      </div>
      <div className="CategoryLineTableRow_distance">{dashIfNullOrEmpty(category.distance)}</div>
      <div className="CategoryLineTableRow_price">
        {round(category.priceObj.value, 2) ?? 0}
        {' '}
        {currency}
      </div>
    </RowContainer>
  );
};

const RowContainer = styled.div`
  ${table};
  ${textP};
  color: var(--color-grey4);

  .CategoryLineTableRow_title {
    ${textH4};
    color: var(--color-dark);
  }

  .CategoryLineTableRow_ageRange {
    text-align: right;
  }

  .CategoryLineTableRow_price {
    text-align: right;
  }

  > div:not(.CategoryLineTableRow_title) {
    max-width: 100px;
  }

  ${mediaBreakpoint.mobile} {
    .CategoryLineTableRow_title {
      grid-area: title;
      margin-top: 3px;
    }

    .CategoryLineTableRow_gender {
      grid-area: gender;
    }

    .CategoryLineTableRow_ageRange {
      text-align: left;
      grid-area: ageRange;
    }

    .CategoryLineTableRow_distance {
      text-align: right;
      grid-area: distance;
    }

    .CategoryLineTableRow_price {
      text-align: right;
      grid-area: price;
    }

    > div:not(.CategoryLineTableRow_title) {
      max-width: unset;
    }
  }
`;
